// Instruction builders for the two "append" instructions: add_subtitle_track
// and add_storage_location. Both extend a video without touching root_hash —
// see the design's §6.4/§6.6 — which is why they are legal even after a video
// is sealed, unlike every other write in this package.
//
// These call `.rpc()` directly rather than returning an unsigned instruction:
// `program` here already carries the connected wallet (built by useProgram),
// so there is nothing left for a caller to do except await the signature.
// Errors are left exactly as Anchor throws them — un-wrapped, cause intact —
// so `humanizeForumError` (which walks `err.error.errorCode` and `err.cause`)
// can still find the program's error code. Wrapping or rethrowing here, the
// way `useSendTransaction` does for its own generic case, would hide it.
import type { Program } from '@coral-xyz/anchor'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import type { Forum } from '../idl/forum'
import { langBytes, rootHashBytes, HASH_ALGO_SHA256 } from './encoding.js'
import { publisherPda, subtitlePda, storagePda } from './pda.js'

/** Index into the program's storage driver enum. Never reused, same
 * discipline as HASH_ALGO_SHA256 in encoding.ts. */
export const DRIVER_HTTPS = 1
export const DRIVER_IPFS = 2
export const DRIVER_FILECOIN = 3

// `program.provider.publicKey` is only unset if `program` was built without a
// connected wallet — `useProgram` never returns a Program in that case, so
// reaching here is a caller bug, not a user-facing state.
function requireAuthority(program: Program<Forum>): PublicKey {
  const authority = program.provider.publicKey
  if (!authority) throw new Error('append.ts: program has no connected wallet to sign with')
  return authority
}

/** Anchors an addendum subtitle track. The video MUST already be sealed on
 * chain — the program enforces this itself (NotSealed) and this function
 * does not duplicate the check. */
export async function addSubtitleTrack(args: {
  program: Program<Forum>
  videoPda: PublicKey
  lang: string // padded to LANG_LEN bytes by langBytes(), same as encoding.ts and chain/tests/forum.ts
  hashHex: string // 64 hex chars
}): Promise<string> {
  const { program, videoPda, lang, hashHex } = args
  const authority = requireAuthority(program)
  const lang8 = langBytes(lang)

  return program.methods
    .addSubtitleTrack(
      lang8,
      // Same shape as a root hash — [u8; 32] from 64 hex chars — so this
      // reuses rootHashBytes rather than a second copy of the same check.
      rootHashBytes(hashHex),
      HASH_ALGO_SHA256,
    )
    .accountsPartial({
      publisher: publisherPda(authority),
      video: videoPda,
      subtitle: subtitlePda(videoPda, lang8),
      authority,
      systemProgram: SystemProgram.programId,
    })
    .rpc()
}

/** Records another place the video's bytes can be fetched from. Legal at any
 * video state, sealed or not.
 *
 * `index` must be the Video account's CURRENT `storage_count`, read fresh off
 * chain immediately before calling this — the PDA is seeded on that number,
 * so a cached or stale index collides with a location someone else already
 * recorded and the transaction fails. See `nextStoragePdaFor` in
 * `chain/tests/forum.ts` for the same rule enforced there. */
export async function addStorageLocation(args: {
  program: Program<Forum>
  videoPda: PublicKey
  index: number
  driver: number
  location: string // max 256 bytes
}): Promise<string> {
  const { program, videoPda, index, driver, location } = args
  const authority = requireAuthority(program)

  return program.methods
    .addStorageLocation(driver, location)
    .accountsPartial({
      publisher: publisherPda(authority),
      video: videoPda,
      storage: storagePda(videoPda, index),
      authority,
      systemProgram: SystemProgram.programId,
    })
    .rpc()
}
